import { useTranslation } from "react-i18next";
import { EmptyState } from "shared/components/root/EmptyState";
import { Flex } from "styled-system/jsx";
import { EventLocationCard } from "./EventLocationCard";
import { getAddressString } from "shared/utils/getAddressString";
import { Address, EventLocation } from "gql/generated";
import { Button, SimpleDialog, Text } from "@ttc3k/trekker";
import { Plus } from "iconoir-react";
import { EventLocationEditModal } from "./EventLocationEditModal";
import { useInPerson } from "./useInPerson";
import { forwardRef, useImperativeHandle, ForwardedRef } from "react";
import { useFormContext } from "react-hook-form";
import { EventCreateEditFormValues } from "../EventCreateEditModal/useEventCreateEditForm";
import { Sortable } from "../../Sortable";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";

export interface InPersonEventRef {
  addNewLocation: () => void;
}

export const InPersonEvent = forwardRef<InPersonEventRef>(
  function InPersonEvent(_, ref: ForwardedRef<InPersonEventRef>) {
    const { t } = useTranslation("events", {
      keyPrefix: "APP.MODAL.EVENT_HOST",
    });

    const {
      eventLocations,
      isEditModalOpen,
      isDeleteConfirmationModalOpen,
      index,
      formFieldRoot,
      locationCopy,
      handleAddLocationClick,
      handleEditLocationClick,
      handleDeleteLocationClick,
      handleDeleteConfirmationModalActionClick,
      handleModalClose,
      handleModalDone,
    } = useInPerson();

    useImperativeHandle(ref, () => ({
      addNewLocation: () => handleAddLocationClick(eventLocations.length),
    }));

    const { formState, setValue } = useFormContext<EventCreateEditFormValues>();

    return (
      <>
        {eventLocations.length === 0 ? (
          <Flex flexDir="column" gap="4px">
            <EmptyState
              ctaButtonProps={{
                children: t("ADD_LOCATION_VENUE"),
                type: "button",
                onClick: () => handleAddLocationClick(eventLocations.length),
              }}
            />
            {formState.errors && formState.errors.step && (
              <Text visual="smallMedium" color="text.error.dark">
                {formState.errors.step?.message ??
                  formState.errors.step?.root?.message}
              </Text>
            )}
          </Flex>
        ) : (
          <Flex flexDir="column" gap="400">
            <Flex flexDir="column" gap="250">
              <Sortable<EventLocation & { _id: string }>
                disabled={false}
                value={eventLocations
                  .filter(
                    // Fixes a fun timing bug where the items in the sortable are not
                    // in sync with the data in the form
                    location => !!location && Object.hasOwn(location, "venue"),
                  )
                  .map(
                    (eventLocation, i) =>
                      ({
                        _id: `${i}-${eventLocation.venue}`,
                        ...eventLocation,
                      }) as EventLocation & { _id: string },
                  )}
                onChange={async newEventLocations => {
                  const locationsWithoutIds = newEventLocations.map(
                    location => {
                      delete (location as { _id?: string })._id;
                      return location;
                    },
                  ) as EventCreateEditFormValues["eventLocations"];
                  setValue("eventLocations", locationsWithoutIds, {
                    shouldDirty: true,
                  });
                }}
                selectorID="_id"
                size="compact"
                sortingStrategy={verticalListSortingStrategy}
                sortableComponent={(_, i) => {
                  const location = eventLocations[i];
                  // Sortable seems to run out of sync with the eventLocations array
                  // - When the array is updated, it causes Sortable to re-render, but
                  //   it renders against the previous array size, to when you remove
                  //   and item, `i` goes larger than the eventLocations array, resulting
                  //   in location being undefined
                  // - This might be caused because eventLocations comes from a useWatch hook
                  // - We return null here to avoid the error
                  if (!location) return null;
                  return (
                    <EventLocationCard
                      key={`${location.venue}-${i}`}
                      index={i}
                      title={location.venue ?? ""}
                      address={getAddressString(
                        (location.contact?.address as Address) ?? {},
                      )}
                      addressError={
                        formState.errors?.eventLocations?.[i]?.root?.message ||
                        formState.errors?.eventLocations?.[i]?.message
                      }
                      onEditClick={() =>
                        handleEditLocationClick(
                          i,
                          location,
                          `eventLocations.${i}`,
                        )
                      }
                      onDeleteClick={() => handleDeleteLocationClick(i)}
                    />
                  );
                }}
              />

              <Button
                visual="unstyled"
                type="button"
                Icon={Plus}
                onClick={() => handleAddLocationClick(eventLocations.length)}
                color={{
                  base: "text.accent.blue.mid",
                  hover: "text.accent.blue.dark",
                  focusVisible: "text.accent.blue.dark",
                }}
                fontSize="16px"
                width="fit-content"
              >
                {t("ADD_LOCATION_VENUE")}
              </Button>
            </Flex>
          </Flex>
        )}
        <EventLocationEditModal
          isOpen={isEditModalOpen}
          onClose={handleModalClose}
          onDone={handleModalDone}
          index={index}
          locationCopy={locationCopy}
          formFieldRoot={formFieldRoot}
          eventLinkControlledInputProps={{
            name: `${formFieldRoot}.contact.links.website`,
            label: t("LOCATION_SELECTION.BUSINESS_WEBSITE"),
          }}
          emailControlledInputProps={{
            name: `${formFieldRoot}.contact.email`,
            label: t("LOCATION_SELECTION.EMAIL"),
          }}
          phoneControlledInputProps={{
            name: `${formFieldRoot}.contact.phone`,
            label: t("LOCATION_SELECTION.PHONE"),
          }}
        />
        <SimpleDialog
          open={isDeleteConfirmationModalOpen}
          title={t("LOCATION_SELECTION.DELETE_MODAL.TITLE")}
          message={t("LOCATION_SELECTION.DELETE_MODAL.MESSAGE")}
          cancelButtonCopy={t("LOCATION_SELECTION.DELETE_MODAL.CANCEL")}
          onCancelClick={() => handleDeleteConfirmationModalActionClick(false)}
          confirmButtonCopy={t("LOCATION_SELECTION.DELETE_MODAL.REMOVE")}
          confirmButtonVisual="destructive"
          onConfirmClick={() => handleDeleteConfirmationModalActionClick(true)}
          size="md"
          portal
        />
      </>
    );
  },
);
